import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import html2pdf from 'html2pdf.js';
import '../../styles/QuotationGenerator.css';


const QuotationGenerator = () => {
  const navigate = useNavigate();
  const quotationRef = useRef();
  const [showPreview, setShowPreview] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkText, setLinkText] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  
  const [quotationData, setQuotationData] = useState({
    refNumber: '',
    date: '',
    customerName: '',
    subject: '',
    content: ''
  });


  const contentRef = useRef(null);


  const handleInputChange = (e) => {
    setQuotationData({ ...quotationData, [e.target.name]: e.target.value });
  };


  const handleContentChange = () => {
    if (contentRef.current) {
      setQuotationData({ ...quotationData, content: contentRef.current.innerHTML });
    }
  };


  const applyFormat = (command, value = null) => {
    document.execCommand(command, false, value);
    contentRef.current.focus();
  };


  const insertLink = () => {
    if (linkText && linkUrl) {
      const url = linkUrl.startsWith('http') ? linkUrl : `https://${linkUrl}`;
      const linkHtml = `<a href="${url}" target="_blank" style="color: #0066cc; text-decoration: underline;">${linkText}</a>`;
      document.execCommand('insertHTML', false, linkHtml);
      setLinkText('');
      setLinkUrl('');
      setShowLinkModal(false);
      contentRef.current.focus();
    }
  };


  const handleDownloadPDF = () => {
    const element = quotationRef.current;
    const filename = `Quotation_${quotationData.refNumber}_${new Date().toISOString().split('T')[0]}.pdf`;
    
    const options = {
      margin: [5, 5, 5, 5],
      filename: filename,
      image: { type: 'png', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { orientation: 'portrait', unit: 'mm', format: 'a4' }
    };


    html2pdf().set(options).from(element).save();
  };


  return (
    <div className="quotation-container">
      <div className="quotation-header-top">
        <button onClick={() => navigate('/admin/dashboard')} className="back-btn">
          ← Back to Dashboard
        </button>
        <h1>Quotation Generator</h1>
      </div>


      {!showPreview ? (
        <div className="quotation-form">
          {/* Quotation Details Section */}
          <div className="form-section">
            <h3>Quotation Details</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Reference Number *</label>
                <input
                  type="text"
                  name="refNumber"
                  value={quotationData.refNumber}
                  onChange={handleInputChange}
                  placeholder="Enter reference number"
                  required
                />
              </div>
              <div className="form-group">
                <label>Date *</label>
                <input
                  type="date"
                  name="date"
                  value={quotationData.date}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>
          </div>


          {/* Customer Details Section */}
          <div className="form-section">
            <h3>Customer Details</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Customer Name (M/s.) *</label>
                <input
                  type="text"
                  name="customerName"
                  value={quotationData.customerName}
                  onChange={handleInputChange}
                  placeholder="Enter customer name"
                  required
                />
              </div>
            </div>
          </div>


          {/* Subject Section */}
          <div className="form-section">
            <h3>Subject</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Subject *</label>
                <input
                  type="text"
                  name="subject"
                  value={quotationData.subject}
                  onChange={handleInputChange}
                  placeholder="Enter subject"
                  required
                />
              </div>
            </div>
          </div>


          {/* Content Section with Rich Text Editor */}
          <div className="form-section">
            <h3>Quotation Content</h3>
            
            {/* Rich Text Editor Toolbar */}
            <div className="editor-toolbar">
              <button 
                type="button" 
                onClick={() => applyFormat('bold')} 
                className="toolbar-btn"
                title="Bold"
              >
                <strong>B</strong>
              </button>
              <button 
                type="button" 
                onClick={() => applyFormat('underline')} 
                className="toolbar-btn"
                title="Underline"
              >
                <u>U</u>
              </button>
              <button 
                type="button" 
                onClick={() => setShowLinkModal(true)} 
                className="toolbar-btn"
                title="Insert Link"
              >
                🔗
              </button>
              <button 
                type="button" 
                onClick={() => applyFormat('insertUnorderedList')} 
                className="toolbar-btn"
                title="Bullet List"
              >
                • List
              </button>
              <button 
                type="button" 
                onClick={() => applyFormat('insertOrderedList')} 
                className="toolbar-btn"
                title="Numbered List"
              >
                1. List
              </button>
            </div>


            {/* Editable Content Area */}
            <div
              ref={contentRef}
              contentEditable
              className="rich-text-editor"
              onInput={handleContentChange}
              placeholder="Enter quotation details, pricing, terms and conditions..."
            ></div>


            <p className="editor-hint">
              💡 Tip: Select text and click toolbar buttons to format. Press Enter for new lines.
            </p>
          </div>



          <div className="action-buttons">
            <button onClick={() => setShowPreview(true)} className="generate-btn">
              Generate Quotation Preview
            </button>
          </div>
        </div>
      ) : (
        <>
         <div className="action-buttons no-print" >
            <button onClick={() => setShowPreview(false)} className="edit-btn" style={{ marginRight: '2rem' }}>
              ← Edit Quotation
            </button>
            <button onClick={handleDownloadPDF} className="download-pdf-btn">
              🖨️ Download PDF
            </button>
          </div>


          <div className="quotation-preview">
            <div className="quotation-document" ref={quotationRef}>
              {/* Header Section */}
              <div className="quotation-header-doc">
                <div className="header-top">
                  <div className="logo-section">
                    <div className="logo-placeholder">
                      <svg width="70" height="70" viewBox="0 0 120 100" xmlns="http://www.w3.org/2000/svg">
                        <rect x="5" y="12" width="110" height="4" fill="#000"/>
                        <path d="M 10,12 L 15,8 L 20,12 L 25,8 L 30,12 L 35,8 L 40,12 L 45,8 L 50,12 L 55,8 L 60,12 L 65,8 L 70,12 L 75,8 L 80,12 L 85,8 L 90,12 L 95,8 L 100,12 L 105,8 L 110,12" 
                              stroke="#000" strokeWidth="1.5" fill="none"/>
                        <polygon points="10,16 15,22 5,22" fill="#000"/>
                        <polygon points="110,16 115,22 105,22" fill="#000"/>
                        <rect x="56" y="16" width="8" height="60" fill="none" stroke="#000" strokeWidth="2"/>
                        <rect x="50" y="22" width="20" height="2" fill="#000"/>
                        <rect x="50" y="30" width="20" height="2" fill="#000"/>
                        <rect x="50" y="38" width="20" height="2" fill="#000"/>
                        <rect x="50" y="46" width="20" height="2" fill="#000"/>
                        <rect x="50" y="54" width="20" height="2" fill="#000"/>
                        <rect x="50" y="62" width="20" height="2" fill="#000"/>
                        <rect x="50" y="70" width="20" height="2" fill="#000"/>
                        <line x1="50" y1="22" x2="56" y2="30" stroke="#000" strokeWidth="1.5"/>
                        <line x1="70" y1="22" x2="64" y2="30" stroke="#000" strokeWidth="1.5"/>
                        <line x1="50" y1="30" x2="56" y2="38" stroke="#000" strokeWidth="1.5"/>
                        <line x1="70" y1="30" x2="64" y2="38" stroke="#000" strokeWidth="1.5"/>
                        <line x1="50" y1="38" x2="56" y2="46" stroke="#000" strokeWidth="1.5"/>
                        <line x1="70" y1="38" x2="64" y2="46" stroke="#000" strokeWidth="1.5"/>
                        <line x1="50" y1="46" x2="56" y2="54" stroke="#000" strokeWidth="1.5"/>
                        <line x1="70" y1="46" x2="64" y2="54" stroke="#000" strokeWidth="1.5"/>
                        <line x1="50" y1="54" x2="56" y2="62" stroke="#000" strokeWidth="1.5"/>
                        <line x1="70" y1="54" x2="64" y2="62" stroke="#000" strokeWidth="1.5"/>
                        <line x1="50" y1="62" x2="56" y2="70" stroke="#000" strokeWidth="1.5"/>
                        <line x1="70" y1="62" x2="64" y2="70" stroke="#000" strokeWidth="1.5"/>
                        <line x1="95" y1="16" x2="95" y2="40" stroke="#000" strokeWidth="2"/>
                        <rect x="91" y="40" width="8" height="6" fill="#000"/>
                        <rect x="40" y="76" width="40" height="4" fill="#000"/>
                        <rect x="45" y="80" width="30" height="3" fill="#000"/>
                        <rect x="10" y="8" width="100" height="2" fill="#0066cc"/>
                      </svg>
                    </div>
                    <div className="company-name">
                      <h1>Bricks & Brain</h1>
                      <p className="tagline">Civil Contractor and Interior Decorator</p>
                    </div>
                  </div>
                  <div className="header-right">
                    <p className="jurisdiction">Subject to Palghar Jurisdiction</p>
                    <p className="mobile">Mobile : 9049610819</p>
                  </div>
                </div>
                <div className="address-line">
                  D-001, Periyar Building, Babhola Naka, Opp. D'mart, Vasai (West), Dist. Palghar - 401 202.
                </div>
              </div>


              {/* Ref Number and Date - SAME LINE */}
              <div className="quotation-meta">
                <div className="quotation-no">
                  <span>Ref. No.: </span><strong>{quotationData.refNumber}</strong>
                </div>
                <div className="quotation-date">
                  <span>Date : </span><strong>{quotationData.date ? new Date(quotationData.date).toLocaleDateString('en-GB').replace(/\//g, '-') : '__________'}</strong>
                </div>
              </div>


              {/* Customer Name */}
              <div className="customer-line">
                <span>M/s. </span>
               <span className="customer-name-underline">{quotationData.customerName}</span>



              </div>


              {/* Subject */}
              <div className="subject-line">
                <span>Subject : </span>
                <span className="subject-underline">{quotationData.subject || '_________________________________________________________________'}</span>
              </div>


              {/* Content Area */}
              <div className="quotation-content-area">
                {quotationData.content ? (
                  <div 
                    className="content-text" 
                    dangerouslySetInnerHTML={{ __html: quotationData.content }}
                  />
                ) : (
                  <div className="blank-space"></div>
                )}
              </div>


              {/* Footer Section */}
              <div className="quotation-footer-original">
                <div className="signature-section-right">
                  <p className="for-company">For Bricks & Brain</p>
                  <p className="proprietor-label">Proprietor</p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}


      {/* Link Modal */}
      {showLinkModal && (
        <div className="modal-overlay" onClick={() => setShowLinkModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Insert Hyperlink</h3>
            <div className="form-group">
              <label>Link Text *</label>
              <input
                type="text"
                value={linkText}
                onChange={(e) => setLinkText(e.target.value)}
                placeholder="Enter text to display"
              />
            </div>
            <div className="form-group">
              <label>URL *</label>
              <input
                type="text"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://example.com"
              />
            </div>
            <div className="modal-buttons">
              <button onClick={insertLink} className="insert-btn">Insert Link</button>
              <button onClick={() => setShowLinkModal(false)} className="cancel-btn">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default QuotationGenerator;
